import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, CheckCircle2, Calendar as CalIcon, Clock, Users, Flower } from "lucide-react";
import { ConsentNote } from "../components/legal/LegalBits";
import { PendingConfirmationAlert } from "../components/PendingConfirmationAlert";
import { SubPageNav } from "../components/SubPageNav";
import { fetchSpaAvailability, createSpaBooking, fetchSpaConfig, fetchRestaurant } from "../lib/api";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import { tr } from "../lib/i18n";
import AppBottomBar from "../components/AppBottomBar";

// UI text dictionaries for the wellness booking page.
const S = {
  back: { it:"Torna ai menu", en:"Back to menus", es:"Volver a los menús", fr:"Retour aux menus", de:"Zurück zu den Menüs" },
  title: { it:"Prenota la Spa", en:"Book the Spa", es:"Reserva el Spa", fr:"Réserver le Spa", de:"Spa buchen" },
  intro: {
    it:"Ingressi privati di 2 ore, minimo 2 e massimo 4 persone. Prenota il tuo turno e paga in struttura all'arrivo.",
    en:"Private 2-hour slots, minimum 2 and maximum 4 guests. Book your slot and pay on arrival.",
    es:"Turnos privados de 2 horas, mínimo 2 y máximo 4 personas. Reserva tu turno y paga en el establecimiento.",
    fr:"Créneaux privés de 2 heures, minimum 2 et maximum 4 personnes. Réservez votre créneau et payez sur place.",
    de:"Private 2-Stunden-Slots, mindestens 2 und höchstens 4 Personen. Reserviere und zahle vor Ort.",
  },
  date: { it:"Data", en:"Date", es:"Fecha", fr:"Date", de:"Datum" },
  slot: { it:"Turno", en:"Time slot", es:"Turno", fr:"Créneau", de:"Zeitfenster" },
  slotPlaceholder: { it:"Seleziona un turno…", en:"Select a slot…", es:"Selecciona un turno…", fr:"Sélectionnez un créneau…", de:"Zeitfenster wählen…" },
  perPerson: { it:"a persona", en:"per person", es:"por persona", fr:"par personne", de:"pro Person" },
  slotFare: { it:"Tariffa", en:"Rate", es:"Tarifa", fr:"Tarif", de:"Tarif" },
  checking: { it:"Verifica disponibilità…", en:"Checking availability…", es:"Comprobando disponibilidad…", fr:"Vérification…", de:"Verfügbarkeit prüfen…" },
  noSlots: { it:"Nessun turno disponibile in questa data. Prova un altro giorno.", en:"No slots available on this date. Try another day.", es:"Sin turnos disponibles. Prueba otro día.", fr:"Aucun créneau disponible. Essayez un autre jour.", de:"Keine Slots verfügbar. Bitte anderen Tag wählen." },
  fullName: { it:"Nome e cognome", en:"Full name", es:"Nombre y apellidos", fr:"Nom et prénom", de:"Vor- und Nachname" },
  email: { it:"Email", en:"Email", es:"Email", fr:"Email", de:"E-Mail" },
  phone: { it:"Telefono", en:"Phone", es:"Teléfono", fr:"Téléphone", de:"Telefon" },
  people: { it:"Numero persone", en:"Number of guests", es:"Número de personas", fr:"Nombre de personnes", de:"Anzahl Personen" },
  peopleMinMax: { it:"min {min}, max {max}", en:"min {min}, max {max}", es:"mín {min}, máx {max}", fr:"min {min}, max {max}", de:"min {min}, max {max}" },
  guests1: { it:"persona", en:"guest", es:"persona", fr:"personne", de:"Person" },
  guestsN: { it:"persone", en:"guests", es:"personas", fr:"personnes", de:"Personen" },
  notes: { it:"Note (opzionale)", en:"Notes (optional)", es:"Notas (opcional)", fr:"Notes (facultatif)", de:"Notizen (optional)" },
  notesPh: { it:"Anniversario, esigenze particolari…", en:"Anniversary, special needs…", es:"Aniversario, necesidades especiales…", fr:"Anniversaire, besoins spéciaux…", de:"Jahrestag, besondere Wünsche…" },
  total: { it:"Totale da pagare in struttura", en:"Total to pay on arrival", es:"Total a pagar al llegar", fr:"Total à régler sur place", de:"Zu zahlen vor Ort" },
  submitInfo: { it:"Confermando la prenotazione riceverai una email di riepilogo. Il pagamento verrà effettuato in struttura all'arrivo.", en:"On confirmation you'll receive a summary email. Payment will be collected on arrival.", es:"Al confirmar recibirás un email de resumen. El pago se realizará al llegar.", fr:"À la confirmation vous recevrez un email récapitulatif. Le paiement se fait sur place.", de:"Nach der Bestätigung erhältst Du eine Zusammenfassung per E-Mail. Zahlung vor Ort." },
  submit: { it:"Prenota adesso", en:"Book now", es:"Reservar ahora", fr:"Réserver", de:"Jetzt buchen" },
  submitting: { it:"Prenotazione in corso…", en:"Booking…", es:"Reservando…", fr:"Réservation…", de:"Buchung läuft…" },
  chooseSlot: { it:"Seleziona un turno", en:"Please choose a slot", es:"Selecciona un turno", fr:"Choisissez un créneau", de:"Zeitfenster wählen" },
  chooseSlotDesc: { it:"Scegli uno dei turni disponibili", en:"Pick one of the available slots", es:"Elige uno de los turnos disponibles", fr:"Choisissez un créneau disponible", de:"Wähle einen verfügbaren Slot" },
  errorTitle: { it:"Impossibile prenotare", en:"Booking failed", es:"No se pudo reservar", fr:"Réservation impossible", de:"Buchung fehlgeschlagen" },
  errorDesc: { it:"Errore imprevisto. Riprova.", en:"Unexpected error. Please retry.", es:"Error inesperado. Reintenta.", fr:"Erreur inattendue. Réessayez.", de:"Unerwarteter Fehler. Bitte erneut versuchen." },
  successTitle: { it:"Richiesta ricevuta", en:"Request received", es:"Solicitud recibida", fr:"Demande reçue", de:"Anfrage erhalten" },
  successThanks: { it:"Grazie", en:"Thank you", es:"Gracias", fr:"Merci", de:"Danke" },
  successSummary: { it:"Ecco il riepilogo", en:"Here is your summary", es:"Aquí tu resumen", fr:"Voici votre récapitulatif", de:"Hier ist Deine Zusammenfassung" },
  slotLabel: { it:"Turno", en:"Slot", es:"Turno", fr:"Créneau", de:"Slot" },
  totalLbl: { it:"Totale", en:"Total", es:"Total", fr:"Total", de:"Summe" },
  toBePaid: { it:"da saldare in struttura", en:"to pay on arrival", es:"a pagar al llegar", fr:"à régler sur place", de:"vor Ort zu zahlen" },
  emailSent: {
    it:"Ti abbiamo inviato una email di conferma a",
    en:"We sent a confirmation email to",
    es:"Te enviamos un email de confirmación a",
    fr:"Un email de confirmation a été envoyé à",
    de:"Wir haben eine Bestätigungs-E-Mail gesendet an",
  },
  cancelInfo: {
    it:"In caso di rinuncia, avvisaci con almeno 24 ore di anticipo.",
    en:"If you need to cancel, please let us know at least 24 hours in advance.",
    es:"Si necesitas cancelar, avísanos con al menos 24 horas de antelación.",
    fr:"Pour annuler, prévenez-nous au moins 24 heures à l'avance.",
    de:"Bei Absage bitte mindestens 24 Stunden vorher Bescheid geben.",
  },
  home: { it:"Torna alla home", en:"Back to home", es:"Volver al inicio", fr:"Retour à l'accueil", de:"Zur Startseite" },
};

function isoTodayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isoInDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const MONTHS_BY_LANG = {
  it: ["gennaio","febbraio","marzo","aprile","maggio","giugno","luglio","agosto","settembre","ottobre","novembre","dicembre"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
  es: ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"],
  fr: ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"],
  de: ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"],
};
const DAYS_BY_LANG = {
  it: ["Domenica","Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato"],
  en: ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
  es: ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"],
  fr: ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"],
  de: ["Sonntag","Montag","Dienstag","Mittwoch","Donnerstag","Freitag","Samstag"],
};

function formatDateIt(iso, lang = "it") {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const days = DAYS_BY_LANG[lang] || DAYS_BY_LANG.it;
  const months = MONTHS_BY_LANG[lang] || MONTHS_BY_LANG.it;
  return `${days[dt.getDay()]} ${d} ${months[m - 1]} ${y}`;
}

export default function SpaBookingPage() {
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [config, setConfig] = useState(null);
  const [date, setDate] = useState(isoTodayLocal());
  const [availability, setAvailability] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ slot_id: "", name: "", email: "", phone: "", num_people: 2, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);

  const minDate = isoTodayLocal();
  const maxDate = useMemo(() => isoInDays(config?.horizon_days || 360), [config]);
  const minPeople = config?.min_people || 2;
  const maxPeople = config?.max_people || 4;

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([fetchRestaurant(), fetchSpaConfig()]);
        setRestaurant(r); setConfig(c);
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingSlots(true);
      try {
        const a = await fetchSpaAvailability(date);
        if (alive) {
          setAvailability(a);
          // reset chosen slot if it became unavailable
          setForm((f) => {
            const still = a.slots.find((s) => s.id === Number(f.slot_id) && !s.booked);
            return still ? f : { ...f, slot_id: "" };
          });
        }
      } catch (e) {
        if (alive) setAvailability(null);
      } finally {
        if (alive) setLoadingSlots(false);
      }
    })();
    return () => { alive = false; };
  }, [date]);

  const chosenSlot = availability?.slots?.find((s) => s.id === Number(form.slot_id));
  const availableSlots = (availability?.slots || []).filter((s) => !s.booked);
  const totalPrice = chosenSlot ? chosenSlot.price_per_person * Number(form.num_people || 0) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (form.slot_id === "" || !chosenSlot) {
      toast({ title: tr(S.chooseSlot, lang), description: tr(S.chooseSlotDesc, lang) });
      return;
    }
    setSubmitting(true);
    try {
      const res = await createSpaBooking({
        date,
        slot_id: Number(form.slot_id),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        num_people: Number(form.num_people),
        notes: form.notes.trim(),
      });
      setSuccess(res);
    } catch (err) {
      const msg = err?.response?.data?.detail || tr(S.errorDesc, lang);
      toast({ title: tr(S.errorTitle, lang), description: msg });
      // refresh availability in case slot was booked meanwhile
      const a = await fetchSpaAvailability(date);
      setAvailability(a);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#faf3ee]">
        <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar />
        <main className="max-w-2xl mx-auto px-4 py-10">
          <div className="rounded-2xl bg-white border border-[#e8dcc8] p-8 shadow-sm text-center">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
            <h1 className="mt-4 font-serif text-3xl font-bold text-[#2b1410]">{tr(S.successTitle, lang)}</h1>
            <p className="mt-2 text-[#7c5a4e]">
              {tr(S.successThanks, lang)} <strong>{success.booking.name}</strong>! {tr(S.successSummary, lang)}:
            </p>
            <div className="mt-6 text-left rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-5 space-y-2">
              <div className="flex items-center gap-2 text-[#2b1410]"><CalIcon className="h-4 w-4 text-[#4a1c17]" /> <strong>{success.date_label}</strong></div>
              <div className="flex items-center gap-2 text-[#2b1410]"><Clock className="h-4 w-4 text-[#4a1c17]" /> {tr(S.slotLabel, lang)} <strong>{success.slot.label}</strong></div>
              <div className="flex items-center gap-2 text-[#2b1410]"><Users className="h-4 w-4 text-[#4a1c17]" /> {success.booking.num_people} {success.booking.num_people === 1 ? tr(S.guests1, lang) : tr(S.guestsN, lang)}</div>
              <div className="text-[#2b1410]">
                {tr(S.totalLbl, lang)}: <strong>€ {success.booking.price}</strong>
                <span className="text-[#7c5a4e]"> (€ {success.booking.price_per_person} × {success.booking.num_people})</span>
                · {tr(S.toBePaid, lang)}
              </div>
            </div>
            <p className="mt-5 text-sm text-[#7c5a4e]">
              {success.email_configured ? (
                <>{tr(S.emailSent, lang)} <strong>{success.booking.email}</strong>. </>
              ) : null}
              {tr(S.cancelInfo, lang)}
            </p>
            <PendingConfirmationAlert lang={lang} phone={restaurant?.info?.phone} />
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-2 text-sm font-medium"
                data-testid="spa-back-home"
              >
                {tr(S.home, lang)}
              </button>
            </div>
          </div>
        </main>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf3ee]">
      <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} compact stickyBar />
      <main className="max-w-3xl mx-auto px-4 py-6" data-testid="spa-booking-page">
        <button onClick={() => navigate("/")} className="hidden" data-testid="spa-back-btn">
          <ChevronLeft className="h-4 w-4" /> {tr(S.back, lang)}
        </button>
        <SubPageNav hideMenu lang={lang} />

        {/* Hero standalone rounded card with looping SPA video, matching /cavallo /bici layout */}
        <section className="relative overflow-hidden rounded-3xl bg-[#2b1410] text-[#f7ebdf] shadow-lg min-h-[320px] sm:min-h-[440px] flex items-center">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/media/hero-spa.mp4"
            poster="/media/hero-spa-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410]/95 via-[#2b1410]/55 to-[#2b1410]/25 pointer-events-none" />
          <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
               style={{ backgroundImage: "radial-gradient(rgba(255,220,180,0.4) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />

          <div className="relative w-full px-6 py-10 sm:px-12 sm:py-14 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/25 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-[#e6c46a]">
              <Flower className="h-3.5 w-3.5" /> Privata · 2 ore
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-6xl font-bold leading-[0.95] uppercase text-[#f7ebdf] drop-shadow-lg">
              {tr(S.title, lang).split(" ").slice(0, -1).join(" ")}<br /><span className="text-[#e6c46a] italic normal-case">{tr(S.title, lang).split(" ").slice(-1)[0]}</span>
            </h1>
            <p className="mt-5 max-w-md mx-auto text-sm sm:text-base text-[#f7ebdf]/95 drop-shadow">
              {tr(S.intro, lang)}
            </p>
          </div>
        </section>

        {/* Booking form — separated card below */}
        <div className="mt-6 rounded-2xl border border-[#e8dcc8] shadow-sm bg-white">
          <form onSubmit={submit} className="p-6 sm:p-8 space-y-5">
            {/* Data */}
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">
                <CalIcon className="h-3.5 w-3.5 inline mr-1" /> {tr(S.date, lang)}
              </label>
              <input
                type="date"
                value={date}
                min={minDate}
                max={maxDate}
                onChange={(e) => setDate(e.target.value)}
                required
                data-testid="spa-date-input"
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              />
              {date && <p className="mt-1 text-xs text-[#7c5a4e]">{formatDateIt(date, lang)}</p>}
            </div>

            {/* Turno */}
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">
                <Clock className="h-3.5 w-3.5 inline mr-1" /> {tr(S.slot, lang)}
              </label>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-sm text-[#7c5a4e] py-2"><Loader2 className="h-4 w-4 animate-spin" /> {tr(S.checking, lang)}</div>
              ) : availableSlots.length === 0 ? (
                <p className="text-sm text-red-700 py-2">{tr(S.noSlots, lang)}</p>
              ) : (
                <select
                  value={form.slot_id}
                  onChange={(e) => setForm({ ...form, slot_id: e.target.value })}
                  required
                  data-testid="spa-slot-select"
                  className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
                >
                  <option value="">{tr(S.slotPlaceholder, lang)}</option>
                  {availableSlots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} — € {s.price_per_person} {tr(S.perPerson, lang)}
                    </option>
                  ))}
                </select>
              )}
              {chosenSlot && (
                <p className="mt-1 text-xs text-[#7c5a4e]">
                  {tr(S.slotFare, lang)}: <strong className="text-[#4a1c17]">€ {chosenSlot.price_per_person}</strong> {tr(S.perPerson, lang)}
                </p>
              )}
            </div>

            {/* Nome */}
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">{tr(S.fullName, lang)}</label>
              <input
                type="text" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required minLength={2}
                data-testid="spa-name-input"
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">{tr(S.email, lang)}</label>
                <input
                  type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  data-testid="spa-email-input"
                  className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">{tr(S.phone, lang)}</label>
                <input
                  type="tel" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                  placeholder="+39..."
                  data-testid="spa-phone-input"
                  className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">
                <Users className="h-3.5 w-3.5 inline mr-1" /> {tr(S.people, lang)} ({tr(S.peopleMinMax, lang).replace("{min}", minPeople).replace("{max}", maxPeople)})
              </label>
              <select
                value={form.num_people}
                onChange={(e) => setForm({ ...form, num_people: Number(e.target.value) })}
                required
                data-testid="spa-people-select"
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              >
                {Array.from({ length: maxPeople - minPeople + 1 }, (_, i) => minPeople + i).map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? tr(S.guests1, lang) : tr(S.guestsN, lang)}</option>
                ))}
              </select>
            </div>

            {chosenSlot && (
              <div className="rounded-xl bg-[#4a1c17] text-[#f7ebdf] p-4" data-testid="spa-total-summary">
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider opacity-75">{tr(S.total, lang)}</div>
                    <div className="mt-1 text-xs opacity-80">
                      € {chosenSlot.price_per_person} × {form.num_people} {Number(form.num_people) === 1 ? tr(S.guests1, lang) : tr(S.guestsN, lang)}
                    </div>
                  </div>
                  <div className="text-3xl font-serif font-bold" data-testid="spa-total-price">
                    € {totalPrice}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1.5">{tr(S.notes, lang)}</label>
              <textarea
                rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={tr(S.notesPh, lang)}
                data-testid="spa-notes-input"
                className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/25"
              />
            </div>

            <div className="pt-2 border-t border-[#e8dcc8]">
              <p className="text-xs text-[#7c5a4e]">
                {tr(S.submitInfo, lang)}
              </p>
              <button
                type="submit"
                disabled={submitting || availableSlots.length === 0}
                data-testid="spa-submit-btn"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-5 py-3 text-base font-medium disabled:opacity-60"
              >
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> {tr(S.submitting, lang)}</>) : tr(S.submit, lang)}
              </button>
              <ConsentNote action="Prenotando" className="mt-3" />
            </div>
          </form>
        </div>
      </main>
      <AppBottomBar current="none" lang={lang} />
      <Toaster />
    </div>
  );
}
